/**
 * Hermes-safe en-GB date formatting (t313u). Luxon asks Intl.DateTimeFormat#formatToParts for month, weekday and
 * AM/PM names whenever the locale isn't plain "en-US", and Hermes on iOS returns no such parts, so every
 * `toFormat('ccc d LLL')` came out as "null 23 null" on the phone. These helpers build the same strings from explicit
 * en-GB tables and the DateTime's own numeric fields, so they never touch Intl. The native app installs
 * `formatGB` as Luxon's `toFormat` at start-up (apps/mobile/src/lib/hermesDates.ts), which covers every shared helper.
 */
import { DateTime } from 'luxon';

/** en-GB short months match the browsers' CLDR output ("Sept", not "Sep"). */
export const GB_MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'] as const;
export const GB_MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;
/** Monday first, indexed by Luxon's `weekday - 1`. */
export const GB_WEEKDAYS_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
export const GB_WEEKDAYS_LONG = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

const pad = (n: number, w = 2) => String(Math.abs(n)).padStart(w, '0');

type Fields = Pick<DateTime, 'year' | 'month' | 'day' | 'weekday' | 'hour' | 'minute' | 'second' | 'millisecond'>;
const TOKENS: Record<string, (d: Fields) => string> = {
  d: d => String(d.day), dd: d => pad(d.day),
  L: d => String(d.month), LL: d => pad(d.month), M: d => String(d.month), MM: d => pad(d.month),
  LLL: d => GB_MONTHS_SHORT[d.month - 1]!, MMM: d => GB_MONTHS_SHORT[d.month - 1]!,
  LLLL: d => GB_MONTHS_LONG[d.month - 1]!, MMMM: d => GB_MONTHS_LONG[d.month - 1]!,
  y: d => String(d.year), yyyy: d => pad(d.year, 4), yy: d => pad(d.year % 100),
  c: d => String(d.weekday), E: d => String(d.weekday),
  ccc: d => GB_WEEKDAYS_SHORT[d.weekday - 1]!, EEE: d => GB_WEEKDAYS_SHORT[d.weekday - 1]!,
  cccc: d => GB_WEEKDAYS_LONG[d.weekday - 1]!, EEEE: d => GB_WEEKDAYS_LONG[d.weekday - 1]!,
  H: d => String(d.hour), HH: d => pad(d.hour),
  h: d => String(d.hour % 12 || 12), hh: d => pad(d.hour % 12 || 12),
  m: d => String(d.minute), mm: d => pad(d.minute), s: d => String(d.second), ss: d => pad(d.second),
  S: d => String(d.millisecond), SSS: d => pad(d.millisecond, 3),
  a: d => (d.hour < 12 ? 'am' : 'pm'),
};

/**
 * Luxon `toFormat` patterns (quoted literals included) rendered with en-GB tables. A token outside the table is handed
 * to `fallback` (Luxon's own formatter) so nothing silently disappears.
 */
export function formatGB(d: DateTime, fmt: string, fallback?: (token: string) => string): string {
  if (!d.isValid) return 'Invalid DateTime';
  let out = '';
  for (let i = 0; i < fmt.length;) {
    const ch = fmt[i]!;
    if (ch === "'") {
      const end = fmt.indexOf("'", i + 1);
      if (end === i + 1) { out += "'"; i += 2; continue; }
      out += end < 0 ? fmt.slice(i + 1) : fmt.slice(i + 1, end);
      i = end < 0 ? fmt.length : end + 1;
      continue;
    }
    if (/[A-Za-z]/.test(ch)) {
      let j = i; while (fmt[j] === ch) j++;
      const token = fmt.slice(i, j);
      const f = TOKENS[token];
      out += f ? f(d) : fallback ? fallback(token) : token;
      i = j;
      continue;
    }
    out += ch; i++;
  }
  return out;
}

/** An instant (ISO string, Date or epoch ms) shown in `zone` (default Europe/London) with a Luxon pattern. */
export function formatInstantGB(at: string | Date | number, fmt: string, zone = 'Europe/London'): string {
  const d = typeof at === 'string' ? DateTime.fromISO(at, { zone: 'utc' }) : typeof at === 'number' ? DateTime.fromMillis(at) : DateTime.fromJSDate(at);
  return formatGB(d.setZone(zone), fmt);
}

/** 12345 → "12,345" without Intl.NumberFormat. */
export const groupThousands = (n: number) => String(Math.trunc(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
